import type { StorageService } from "@/persistence/StorageService";

export interface MetaData {
  version: 1;
  runsCompleted: number;
  bestDepth: number;
  notes: string[];
}

export const META_KEY = "game.meta";
export const DEFAULT_META: MetaData = {
  version: 1,
  runsCompleted: 0,
  bestDepth: 0,
  notes: [],
};

const NOTE_POOL: { note: string; unlocksAt: number }[] = [
  {
    note: "Tip from a past run: the auth service resents being renamed.",
    unlocksAt: 1,
  },
  {
    note: "Past-run note: never rebase against the repo root before coffee.",
    unlocksAt: 2,
  },
  {
    note: "Past-run note: the heisenbug hides when you call it by name.",
    unlocksAt: 2,
  },
  {
    note: "Past-run note: the Senior is defeated by evidence, not apologies.",
    unlocksAt: 3,
  },
  {
    note: "Past-run note: the DELETE with no WHERE still sings at night.",
    unlocksAt: 4,
  },
];

export class MetaStore {
  private meta: MetaData = { ...DEFAULT_META };

  constructor(
    private readonly storage: StorageService,
    private readonly key: string = META_KEY
  ) {}

  async load(): Promise<void> {
    const stored = await this.storage.get<MetaData>(this.key);
    if (stored && stored.version === 1) {
      this.meta = { ...DEFAULT_META, ...stored };
    }
  }

  beginRun(): void {
    // Runs are tracked on completion; runsCompleted counts terminated runs.
  }

  recordRun(depth: number, survived: boolean): void {
    this.meta.runsCompleted += 1;
    this.meta.bestDepth = Math.max(
      this.meta.bestDepth,
      survived ? depth : depth
    );
    const noteIdx = Math.min(this.meta.runsCompleted, NOTE_POOL.length);
    const pool = NOTE_POOL.filter(
      (n) => n.unlocksAt <= this.meta.runsCompleted
    );
    for (const candidate of pool) {
      if (!this.meta.notes.includes(candidate.note)) {
        this.meta.notes.push(candidate.note);
      }
    }
    void noteIdx; // (index kept for future reference)
  }

  nextNote(): string | null {
    if (this.meta.notes.length === 0) return null;
    return (
      this.meta.notes[Math.floor(Math.random() * this.meta.notes.length)] ??
      null
    );
  }

  get runsCompleted(): number {
    return this.meta.runsCompleted;
  }

  get bestDepth(): number {
    return this.meta.bestDepth;
  }

  async persist(): Promise<void> {
    await this.storage.set<MetaData>(this.key, this.meta);
  }
}
